import {
  Button,
  Dropdown,
  DropdownItem,
  DropdownMenu,
  DropdownTrigger,
  Input,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  Select,
  SelectItem,
  Switch,
  Textarea,
  Chip,
} from "@nextui-org/react";

import React, { useEffect, useState } from "react";
import { FieldList } from "../../utils/constant";
import { FaChevronDown } from "react-icons/fa";
import { getDataWithFilterUrl } from "../../controllers/APIController";
import {
  fileTypes,
  FrameworkSelectfieldSchema,
  initalValidationError,
  intialFormData,
} from "../../constant/FormBuilder/modal.constant";
import FrameworkInputFieldAccodian from "../../components/Formbuilder/FrameworkInputFieldAccodian";
import { MultiSelectInput } from "./InputField/InputField.jsx";
import {
  CategoryList,
  AVAILABLE_USER_ROLES,
} from "../../constant/FormBuilder/FormBuilderConstant";

const AddModal = ({
  onSubmitHandler,
  id,
  isOpen,
  onOpenChange,
  fieldType,
  frameWorkList,
  frameWorkDetails,
  category,
  existingFields = [],
}) => {
  const [data, setData] = useState(() => {
    console.log("🔍 AddModal: Initializing with intialFormData:", intialFormData);
    return intialFormData;
  });
  const [relatedToList, setRelatedToList] = useState([]);
  const [frameWorkData, setFrameWorkData] = useState([]);
  const [dependentFieldOptions, setDependentFieldOptions] = useState([]);
  const [validationErrors, setValidationErrors] = useState({
    ...initalValidationError,
  });
  const [dynamicCategories, setDynamicCategories] = useState([]);
  const [isLoadingCategories, setIsLoadingCategories] = useState(false);
  const [categoriesError, setCategoriesError] = useState(false);
  // State for dynamic categories and roles
  const [dynamicRoles, setDynamicRoles] = useState([]);
  const [isLoadingRoles, setIsLoadingRoles] = useState(false);
  const [rolesError, setRolesError] = useState(false);
  
  // State for user preview based on selected roles
  const [userPreview, setUserPreview] = useState([]);
  const [isLoadingUserPreview, setIsLoadingUserPreview] = useState(false);
  const [userPreviewError, setUserPreviewError] = useState(false);

  const UserLogged = JSON.parse(sessionStorage.getItem("userData"));
  const OrgInfo = UserLogged && UserLogged.user.LastOrgId;
  const orgFilterUrl = "filters[Organization][id][$eqi]=" + OrgInfo;

  // Use centralized roles constant

  // Fetch categories from metrics API
  useEffect(() => {
    const fetchCategories = async () => {
      if (!OrgInfo) return;
      
      setIsLoadingCategories(true);
      try {
        const data = await getDataWithFilterUrl("metrics", orgFilterUrl);
        const metricsList = data?.data || [];
        
        // Extract unique categories from metrics data
        const uniqueCategories = [...new Set(
          metricsList.map(item => item?.attributes?.Category).filter(Boolean)
        )].sort();
        
        // Convert to the format expected by the dropdown
        const categoriesList = uniqueCategories.map((category, index) => ({
          id: (index + 1).toString(),
          name: category,
        }));
        
        setDynamicCategories(categoriesList);
      } catch (error) {
        console.error("Error fetching categories:", error);
        setCategoriesError(true);
        // Fallback to hardcoded categories if API fails
        setDynamicCategories(CategoryList);
      } finally {
        setIsLoadingCategories(false);
      }
    };

    fetchCategories();
  }, [OrgInfo, orgFilterUrl, fieldType]);

  // Set roles immediately since they're hardcoded
  useEffect(() => {
    setDynamicRoles(AVAILABLE_USER_ROLES);
    setIsLoadingRoles(false);
  }, []);

  // Fetch users based on selected roles for preview
  const fetchUsersByRoles = async (selectedRoles) => {
    // Always fetch users for preview, regardless of role selection
    // This ensures users can always see the list of users in their organization

    setIsLoadingUserPreview(true);
    setUserPreviewError(false);
    
    try {
      // Use the working approach: fetch all users without filter, then filter client-side
      const usersData = await getDataWithFilterUrl("users", "");
      const usersList = usersData?.data || [];
      
      if (usersList.length === 0) {
        setUserPreview([]);
        return;
      }
      
      // Filter users by organization using the same logic as User Management
      const orgUsers = usersList.filter(user => {
        // Check if user belongs to current organization using OrganizationLists
        const organizationLists = user?.attributes?.OrganizationLists?.data || [];
        let isMemberOfCurrentOrg = false;
        
        organizationLists.forEach((org) => {
          if (org.id === OrgInfo) {
            isMemberOfCurrentOrg = true;
          }
        });
        
        return isMemberOfCurrentOrg;
      });
      
      // Map users with role information based on selected roles
      const filteredUsers = orgUsers.map(user => {
        // For now, assign a role based on the selected roles (simplified)
        // In the future, this can be enhanced to get actual role from user data
        const selectedRoleIds = Array.isArray(selectedRoles) ? selectedRoles : [selectedRoles];
        const role = dynamicRoles.find(r => r.id.toString() === selectedRoleIds[0]?.toString());
        
        return {
          id: user.id,
          name: user?.attributes?.username || user?.attributes?.Username || user?.attributes?.Name || 'Unknown User',
          email: user?.attributes?.email || user?.attributes?.Email || 'No email',
          role: role ? role.name : 'User'
        };
      });
      
      setUserPreview(filteredUsers);
    } catch (error) {
      console.error("Error fetching users for preview:", error);
      setUserPreviewError(true);
      setUserPreview([]);
    } finally {
      setIsLoadingUserPreview(false);
    }
  };

  // Update user preview when role selection changes
  useEffect(() => {
    fetchUsersByRoles(data?.userRoleFilter);
  }, [data?.userRoleFilter, OrgInfo]);

  const validationHandler = () => {
    let isValid = true;
    const newValidationError = { ...initalValidationError };
    if (data?.title === "") {
      newValidationError["title"] = "Title can't be empty";
      isValid = false;
    }
    if (data?.relatedTo === "" && fieldType === "relation") {
      newValidationError["relatedTo"] = "relatedTo can't be empty";
      isValid = false;
    }
    if (data?.valuesList === "" && fieldType === "dropdown") {
      newValidationError["valueList"] = "valuesList can't be empty";
      isValid = false;
    }
    if (data?.metricName === "" && fieldType === "relation") {
      newValidationError["metricName"] = "metricName can't be empty";
      isValid = false;
    }
    if (data?.linkUrl === "" && fieldType === "link") {
      newValidationError["linkUrl"] = "Url can't be empty";
      isValid = false;
    }
    if (data?.dependentField === "" && fieldType === "conditional") {
      newValidationError["dependentField"] = "Dependent field can't be empty";
      isValid = false;
    }
    if (data?.triggerValue === "" && fieldType === "conditional") {
      newValidationError["triggerValue"] = "Trigger value can't be empty";
      isValid = false;
    }
    if (
      category?.toLowerCase() !== "reference data" &&
      frameWorkData?.length === 0
    ) {
      newValidationError["Framework"] = "select at least one framework";
      isValid = false;
    }
    setValidationErrors({ ...newValidationError });

    return isValid;
  };
  const submitHandler = (onClose) => {
    const isValid = validationHandler();
    if (isValid === false) return;
    
    // Debug logging
    console.log("AddModal received fieldType:", fieldType);
    console.log("Available field types:", Object.keys(FieldList));
    
    // Safety check for valid field type
    if (!FieldList[fieldType]) {
      console.error(`Invalid field type: ${fieldType}`);
      return;
    }
    
    const fieldInfo = {
      id: id,
      Title: data?.title,
      Required: data?.required,
      framework: frameWorkData,
      ...(fieldType !== "subheading" && { assignedGroup: data?.assignedGroup || "" }),
    };
    if (fieldType === "text" || fieldType === "textarea") {
      onSubmitHandler({
        ...fieldInfo,
        Type: fieldType,
        name: FieldList[fieldType].title,
        char_limit: +data?.charLimit,
      });
    } else if (fieldType === "integer" || fieldType === "slider") {
      onSubmitHandler({
        ...fieldInfo,
        Type: fieldType,
        name: FieldList[fieldType].title,
        min_val: +data?.minValue,
        max_val: +data?.maxValue,
      });
    } else if (fieldType === "slider") {
      onSubmitHandler({
        ...fieldInfo,
        Type: fieldType,
        name: FieldList[fieldType].title,
        min_val: +data?.minValue,
        max_val: +data?.maxValue,
        step_val: data?.stepValue,
      });
    } else if (fieldType === "relation") {
      onSubmitHandler({
        ...fieldInfo,
        Type: fieldType,
        relatedTo: data?.relatedTo,
        metricName: data?.metricName,
        name: FieldList[fieldType].title,
        isMultiSelect: data?.isMultiSelect,
      });
    } else if (fieldType === "dropdown") {
      const values = data?.valuesList.split(",").map((item, index) => {
        return {
          id: index,
          name: item.trim(),
        };
      });

      onSubmitHandler({
        ...fieldInfo,
        Type: fieldType,
        values: values,
        name: FieldList[fieldType].title,
        isMultiSelect: data?.isMultiSelect,
      });
    } else if (fieldType === "link") {
      onSubmitHandler({
        ...fieldInfo,
        link: data?.linkUrl,
        Type: "link",
        name: "link",
      });
    } else if (fieldType === "file") {
      onSubmitHandler({
        ...fieldInfo,
        Type: fieldType,
        name: FieldList[fieldType].title,
        acceptFieldType: data?.acceptFieldType,
      });
    } else if (fieldType === "conditional") {
      onSubmitHandler({
        ...fieldInfo,
        Type: fieldType,
        name: FieldList[fieldType].title,
        dependentField: data?.dependentField,
        triggerValue: data?.triggerValue,
        dependentFieldType: data?.dependentFieldType,
        dependentFieldConfig: data?.dependentFieldConfig,
      });
    } else if (fieldType === "subheading") {
      onSubmitHandler({
        ...fieldInfo,
        Type: fieldType,
        name: FieldList[fieldType].title,
        headingLevel: data?.headingLevel || "h3",
        subheadingDescription: data?.subheadingDescription || "",
      });
    } else if (fieldType === "metricSelector") {
      onSubmitHandler({
        ...fieldInfo,
        Type: fieldType,
        name: FieldList[fieldType].title,
        metricCategoryFilter: data?.metricCategoryFilter || "",
      });
    } else if (fieldType === "tree") {
      // Parse initial tree data if provided
      let initialData = [];
      if (data?.initialTreeData) {
        try {
          initialData = JSON.parse(data.initialTreeData);
        } catch (e) {
          console.error("Invalid JSON in initial tree data:", e);
          // Show error to user
          alert("Invalid JSON format. Please check your JSON structure.");
          return;
        }
      }
      
      onSubmitHandler({
        ...fieldInfo,
        Type: fieldType,
        name: FieldList[fieldType].title,
        initialTreeData: initialData,
      });
    } else {
      onSubmitHandler({
        ...fieldInfo,
        Type: fieldType,
        name: FieldList[fieldType].title,
      });
    }

    onClose();
  };

  const handleKeyDown = (event, onClose) => {
    if (event.key === "Enter") {
      event.preventDefault(); // Prevent the default action of form submission on Enter key press
      submitHandler(onClose);
    }
  };

  useEffect(() => {
    if (data?.metricName === "") return;

    const filteredUrl = "filters[Category][$eqi]=" + data?.metricName.trim();

    getDataWithFilterUrl("metrics", filteredUrl)
      .then((data) => {
        let response = data.data;

        const list = response.map((item) => {
          return {
            id: item.id,
            name: item?.attributes?.Name,
          };
        });
        list.sort((a, b) => {
          if (a.name.toLowerCase() < b.name.toLowerCase()) {
            return -1;
          }
          if (a.name.toLowerCase() > b.name.toLowerCase()) {
            return 1;
          }
          return 0;
        });
        setRelatedToList(list);
      })
      .catch((err) => {
        console.log(err);
      });
  }, [data?.metricName]);

  useEffect(() => {
    setValidationErrors({ ...initalValidationError });
  }, [isOpen]);

  // Update dependent field options when dependent field changes
  useEffect(() => {
    if (data?.dependentField && existingFields?.length > 0) {
      const selectedField = existingFields.find(field => field.Title === data.dependentField);
      
      if (selectedField && selectedField.values) {
        setDependentFieldOptions(selectedField.values);
      } else {
        setDependentFieldOptions([]);
      }
    } else {
      setDependentFieldOptions([]);
    }
  }, [data?.dependentField, existingFields]);

  return (
    <>
      <Modal
        isOpen={isOpen}
        onOpenChange={onOpenChange}
        size="3xl"
        classNames={{
          base: "bg-white border border-slate-200 shadow-lg",
          header: "border-b border-slate-200 bg-slate-50",
          body: "py-6",
          footer: "border-t border-slate-200 bg-slate-50",
        }}
      >
        <ModalContent>
          {(onClose) => (
            <div
              onKeyDown={(event) => handleKeyDown(event, onClose)}
              tabIndex="0"
              className="outline-none"
            >
              <ModalHeader className="flex flex-col gap-2 px-6 py-4">
                <div className="flex items-center justify-between w-full">
                  <div>
                    <h3 className="text-lg font-semibold text-slate-900">Add a {fieldType} Field</h3>
                    <p className="text-sm text-slate-600 mt-1">Configure your new form field</p>
                  </div>
                  <Chip color="primary" variant="flat" size="sm">
                    {fieldType}
                  </Chip>
                </div>
              </ModalHeader>
              <ModalBody className="px-6">
                <div className="overflow-y-auto max-h-[500px] custom-scrollbar space-y-6">
                  <Input
                    type="text"
                    className="mb-8"
                    isRequired
                    label={fieldType === "link" ? "Name" : "Title"}
                    placeholder=" "
                    variant="faded"
                    labelPlacement="outside"
                    value={data?.title || ""}
                    onChange={(event) => {
                      setData({ ...data, title: event.target.value });
                    }}
                    isInvalid={validationErrors["title"] !== ""}
                    errorMessage={validationErrors["title"]}
                  />
                  {/* These input field for text and textarea Input Type  */}
                  {(fieldType === "text" || fieldType === "textarea") && (
                    <>
                      <Input
                        type="number"
                        className="mb-8"
                        label="Char Limit"
                        placeholder=" "
                        variant="faded"
                        labelPlacement="outside"
                        value={data?.charLimit}
                        onChange={(event) => {
                          setData({ ...data, charLimit: event.target.value });
                        }}
                      />
                    </>
                  )}
                  {/* These input field for Integer and Slider Input Type  */}
                  {(fieldType === "integer" || fieldType === "slider") && (
                    <>
                      <Input
                        type="number"
                        className="mb-8"
                        label="Min Value"
                        placeholder={data?.minValue}
                        variant="faded"
                        labelPlacement="outside"
                        value={data?.minValue}
                        onChange={(event) => {
                          setData({ ...data, minValue: event.target.value });
                        }}
                      />
                      <Input
                        type="number"
                        className="mb-8"
                        label="Max Value"
                        placeholder={data?.maxValue}
                        variant="faded"
                        labelPlacement="outside"
                        value={data?.maxValue}
                        onChange={(event) => {
                          setData({ ...data, maxValue: event.target.value });
                        }}
                      />
                    </>
                  )}
                  {/* These input field for Slider Input Type  */}
                  {fieldType === "slider" && (
                    <Input
                      type="text"
                      className="mb-8"
                      label="Step Value"
                      placeholder=" "
                      variant="faded"
                      labelPlacement="outside"
                      value={data?.stepValue}
                      onChange={(event) => {
                        setData({ ...data, stepValue: event.target.value });
                      }}
                    />
                  )}
                  {/* These input field for Relation Input Type  */}
                  {fieldType === "relation" && (
                    <>
                      <div className="flex flex-col gap-2 text-sm w-full">
                        <p
                          className={`${
                            validationErrors["metricName"] && "text-[#f76497]"
                          }`}
                        >
                          Category Name <span style={{ color: "red" }}>*</span>
                        </p>
                        {validationErrors["metricName"] && (
                          <p className="text-[#f76497] mt-0">
                            {validationErrors["metricName"]}
                          </p>
                        )}
                        <Dropdown>
                          <DropdownTrigger className="w-full">
                            <Button className="mb-8" variant="faded" isLoading={isLoadingCategories}>
                              <div className="w-full flex justify-between">
                                <div className="flex justify-start w-full items-start">
                                  {isLoadingCategories ? "Loading categories..." : data?.metricName}
                                </div>
                                <div className="flex justify-end w-full items-end">
                                  <FaChevronDown />
                                </div>
                              </div>
                            </Button>
                          </DropdownTrigger>
                          <DropdownMenu
                            aria-label="Static Actions"
                            className="w-144 custom-dropdown-menu"
                            selectionMode="single"
                            name="categoryName"
                            style={{ zIndex: 9999 }}
                            onSelectionChange={(event) => {
                              setData({
                                ...data,
                                metricName: Array.from(event)
                                  .join(", ")
                                  .replaceAll("_", " "),
                                relatedTo: "",
                              });
                            }}
                          >
                            {dynamicCategories?.map((item) => (
                              <DropdownItem key={item.name}>
                                {item.name}
                              </DropdownItem>
                            ))}
                          </DropdownMenu>
                        </Dropdown>
                      </div>

                      {/* RelatedTo field DropDown  */}

                      <div className="flex flex-col gap-2 text-sm w-full">
                        <Select
                          label="Related To"
                          placeholder="Choose single/multiple field"
                          variant="faded"
                          selectionMode="multiple"
                          labelPlacement="outside"
                          className="my-8"
                          isRequired
                          // selectedKeys={relatedTo.join(',')}
                          onSelectionChange={(event) =>
                            setData({
                              ...data,
                              relatedTo: Array.from(event)
                                .join(",")
                                .replaceAll("_", " "),
                            })
                          }
                        >
                          {relatedToList?.map((item) => (
                            <SelectItem
                              key={item.name}
                              value={item.name}
                              className="py-2"
                            >
                              {item.name}
                            </SelectItem>
                          ))}
                        </Select>
                      </div>
                    </>
                  )}

                  {/* These fields for specific DropDown Field  */}

                  {fieldType === "dropdown" && (
                    <>
                      <Textarea
                        className="mb-8"
                        label={"Enter Values Option by Space-Separate"}
                        key="1"
                        variant="faded"
                        labelPlacement="outside"
                        isRequired
                        value={data?.valuesList}
                        placeholder="value1, values2, values3"
                        onChange={(event) =>
                          setData({ ...data, valuesList: event.target.value })
                        }
                        isInvalid={validationErrors["valueList"] !== ""}
                        errorMessage={validationErrors["valueList"]}
                      />
                    </>
                  )}

                  {/* These fields for specific File Field  */}

                  {fieldType === "file" && (
                    <div className="flex flex-col gap-2 text-sm w-full">
                      <Select
                        label="Files Accepted"
                        placeholder=" "
                        variant="faded"
                        selectionMode="multiple"
                        labelPlacement="outside"
                        className="my-8"
                        isRequired
                        // selectedKeys={relatedTo.join(',')}
                        onSelectionChange={(event) => {
                          setData({
                            ...data,
                            acceptFieldType: Array.from(event),
                          });
                        }}
                      >
                        {fileTypes?.map((item) => (
                          <SelectItem
                            key={item.key}
                            value={item.name}
                            className="py-2"
                          >
                            {item.name}
                          </SelectItem>
                        ))}
                      </Select>
                    </div>
                  )}
                  {/* These fields for specific Link Field  */}

                  {fieldType === "link" && (
                    <Input
                      type="text"
                      className="mb-8"
                      isRequired
                      label="URL"
                      placeholder=" "
                      variant="faded"
                      labelPlacement="outside"
                      value={data?.linkUrl || ""}
                      onChange={(event) => {
                        setData({ ...data, linkUrl: event.target.value });
                      }}
                      isInvalid={validationErrors["linkUrl"] !== ""}
                      errorMessage={validationErrors["linkUrl"]}
                      // onKeyDown={(event) => handleKeyDown(event, onClose)}
                    />
                  )}

                  {/* These fields for specific Subheading Field  */}

                  {fieldType === "subheading" && (
                    <>
                      <div className="flex flex-col gap-2 text-sm w-full">
                        <p>
                          Heading Level
                        </p>
                        <Dropdown>
                          <DropdownTrigger className="w-full">
                            <Button className="mb-8" variant="faded">
                              <div className="w-full flex justify-between">
                                <div className="flex justify-start w-full items-start">
                                  {data?.headingLevel || "Select heading level"}
                                </div>
                                <div className="flex justify-end w-full items-end">
                                  <FaChevronDown />
                                </div>
                              </div>
                            </Button>
                          </DropdownTrigger>
                          <DropdownMenu
                            aria-label="Heading Level"
                            className="w-144"
                            selectionMode="single"
                            style={{ zIndex: 9999 }}
                            onSelectionChange={(event) => {
                              const selectedValue = Array.from(event)[0];
                              setData({
                                ...data,
                                headingLevel: selectedValue,
                              });
                            }}
                          >
                            <DropdownItem key="h2">Large Heading (H2)</DropdownItem>
                            <DropdownItem key="h3">Medium Heading (H3)</DropdownItem>
                            <DropdownItem key="h4">Small Heading (H4)</DropdownItem>
                          </DropdownMenu>
                        </Dropdown>
                      </div>

                      <Textarea
                        type="text"
                        className="my-4"
                        variant="faded"
                        label="Description (Optional)"
                        labelPlacement="outside"
                        placeholder="Add an optional description for this section"
                        name="subheadingDescription"
                        value={data?.subheadingDescription || ""}
                        onChange={(event) =>
                          setData({
                            ...data,
                            subheadingDescription: event.target.value,
                          })
                        }
                      />
                    </>
                  )}

                  {/* These fields for specific Tree Structure Field  */}

                  {fieldType === "tree" && (
                    <>
                      <div className="flex flex-col gap-2 text-sm w-full">
                        <p>Tree Structure Configuration</p>
                        <div className="text-xs text-gray-600 mb-4">
                          Create a hierarchical tree structure with embedded form fields. You can use the GUI editor or paste JSON directly.
                        </div>
                        
                        {/* Features Overview */}
                        <div className="border border-gray-200 rounded-lg p-4 bg-emerald-50 mb-4">
                          <div className="text-sm font-medium mb-2 text-emerald-800">Features:</div>
                          <div className="text-xs text-emerald-700 space-y-1">
                            <div>🌳 Create unlimited nested levels</div>
                            <div>📝 Add any field type (text, dropdown, date, etc.) to any node</div>
                            <div>✏️ Edit node titles and descriptions inline</div>
                            <div>🗑️ Remove nodes and fields easily</div>
                            <div>💾 All data is automatically saved</div>
                            <div>📄 JSON import/export for advanced users</div>
                          </div>
                        </div>

                        {/* Sample Use Cases */}
                        <div className="border border-gray-200 rounded-lg p-4 bg-gray-50 mb-4">
                          <div className="text-sm font-medium mb-2">Perfect for:</div>
                          <div className="text-xs text-gray-600 space-y-1">
                            <div>• Standards mapping and compliance frameworks</div>
                            <div>• Organizational hierarchies with data collection</div>
                            <div>• Process workflows with embedded forms</div>
                            <div>• Risk assessment trees</div>
                            <div>• Audit checklists with nested requirements</div>
                          </div>
                        </div>

                        {/* JSON Configuration Section */}
                        <div className="border border-gray-200 rounded-lg p-4 bg-emerald-50 mb-4">
                          <div className="text-sm font-medium mb-2 text-emerald-800">JSON Configuration (Optional)</div>
                          <div className="text-xs text-emerald-700 mb-3">
                            Paste JSON structure to pre-populate the tree, or leave empty to build manually using the GUI editor.
                          </div>
                          
                          <div className="flex gap-2 mb-4">
                            <Button
                              size="sm"
                              variant="flat"
                              onClick={() => {
                                const sampleData = [
                                  {
                                    id: "standards_framework",
                                    title: "Standards Framework",
                                    description: "Main framework for sustainability standards",
                                    children: [
                                      {
                                        id: "environmental",
                                        title: "Environmental Standards",
                                        description: "Environmental compliance standards",
                                        children: [
                                          {
                                            id: "iso14001",
                                            title: "ISO 14001",
                                            description: "Environmental Management System",
                                            fields: [
                                              {
                                                id: "cert_date",
                                                type: "date",
                                                name: "certificationDate",
                                                title: "Certification Date",
                                                placeholder: "YYYY-MM-DD",
                                                required: true
                                              },
                                              {
                                                id: "status",
                                                type: "dropdown",
                                                name: "status",
                                                title: "Status",
                                                placeholder: "Select status",
                                                values: [
                                                  { id: "active", name: "Active" },
                                                  { id: "inactive", name: "Inactive" },
                                                  { id: "pending", name: "Pending" }
                                                ],
                                                required: true
                                              }
                                            ]
                                          },
                                          {
                                            id: "ghg_protocol",
                                            title: "GHG Protocol",
                                            description: "Greenhouse Gas Accounting",
                                            fields: [
                                              {
                                                id: "scope1",
                                                type: "integer",
                                                name: "scope1",
                                                title: "Scope 1 Emissions",
                                                placeholder: "Metric tons CO2e",
                                                required: true
                                              },
                                              {
                                                id: "scope2",
                                                type: "integer",
                                                name: "scope2",
                                                title: "Scope 2 Emissions",
                                                placeholder: "Metric tons CO2e",
                                                required: true
                                              },
                                              {
                                                id: "scope3",
                                                type: "integer",
                                                name: "scope3",
                                                title: "Scope 3 Emissions",
                                                placeholder: "Metric tons CO2e",
                                                required: false
                                              }
                                            ]
                                          }
                                        ]
                                      },
                                      {
                                        id: "social",
                                        title: "Social Standards",
                                        description: "Social responsibility standards",
                                        children: [
                                          {
                                            id: "sa8000",
                                            title: "SA8000",
                                            description: "Social Accountability Standard",
                                            fields: [
                                              {
                                                id: "cert_level",
                                                type: "dropdown",
                                                name: "certificationLevel",
                                                title: "Certification Level",
                                                placeholder: "Select level",
                                                values: [
                                                  { id: "basic", name: "Basic" },
                                                  { id: "advanced", name: "Advanced" }
                                                ],
                                                required: true
                                              },
                                              {
                                                id: "audit_date",
                                                type: "date",
                                                name: "auditDate",
                                                title: "Last Audit Date",
                                                placeholder: "YYYY-MM-DD",
                                                required: true
                                              }
                                            ]
                                          }
                                        ]
                                      }
                                    ]
                                  }
                                ];
                                setData({
                                  ...data,
                                  initialTreeData: JSON.stringify(sampleData, null, 2),
                                });
                              }}
                              className="bg-emerald-100 hover:bg-emerald-200 text-emerald-800"
                            >
                              Load Sample
                            </Button>
                            <Button
                              size="sm"
                              variant="flat"
                              onClick={() => {
                                setData({
                                  ...data,
                                  initialTreeData: "",
                                });
                              }}
                              className="bg-gray-100 hover:bg-gray-200 text-gray-800"
                            >
                              Clear
                            </Button>
                          </div>

                          <Textarea
                            type="text"
                            className="my-4"
                            variant="faded"
                            label="Initial Tree Data (Optional)"
                            labelPlacement="outside"
                            placeholder="Paste JSON structure or leave empty to build manually using the GUI editor"
                            name="initialTreeData"
                            value={data?.initialTreeData || ""}
                            onChange={(event) =>
                              setData({
                                ...data,
                                initialTreeData: event.target.value,
                              })
                            }
                          />
                        </div>

                        <div className="text-center text-sm text-gray-600">
                          Choose your preferred method: Use the GUI editor for visual building, or paste JSON for advanced users!
                        </div>
                      </div>
                    </>
                  )}

                  {/* These fields for specific Conditional Field  */}

                  {fieldType === "conditional" && (
                    <>
                      <div className="flex flex-col gap-2 text-sm w-full">
                        <p
                          className={`${
                            validationErrors["dependentField"] && "text-[#f76497]"
                          }`}
                        >
                          Dependent Dropdown Field <span style={{ color: "red" }}>*</span>
                        </p>
                        {validationErrors["dependentField"] && (
                          <p className="text-[#f76497] mt-0">
                            {validationErrors["dependentField"]}
                          </p>
                        )}
                        <Dropdown>
                          <DropdownTrigger className="w-full">
                            <Button className="mb-8" variant="faded">
                              <div className="w-full flex justify-between">
                                <div className="flex justify-start w-full items-start">
                                  {data?.dependentField || "Select a dropdown field"}
                                </div>
                                <div className="flex justify-end w-full items-end">
                                  <FaChevronDown />
                                </div>
                              </div>
                            </Button>
                          </DropdownTrigger>
                          <DropdownMenu
                            aria-label="Static Actions"
                            className="w-144 custom-dropdown-menu"
                            selectionMode="single"
                            style={{ zIndex: 9999 }}
                            onSelectionChange={(event) => {
                              const selectedValue = Array.from(event)[0];
                              setData({
                                ...data,
                                dependentField: selectedValue,
                              });
                            }}
                          >
                            {/* This will be populated with existing dropdown fields */}
                            {existingFields
                              ?.filter(field => field.Type === "dropdown")
                              ?.map((field) => (
                              <DropdownItem key={field.Title}>
                                {field.Title}
                              </DropdownItem>
                            ))}
                          </DropdownMenu>
                        </Dropdown>
                      </div>

                      <div className="flex flex-col gap-2 text-sm w-full">
                        <p
                          className={`${
                            validationErrors["triggerValue"] && "text-[#f76497]"
                          }`}
                        >
                          Trigger Value <span style={{ color: "red" }}>*</span>
                        </p>
                        {validationErrors["triggerValue"] && (
                          <p className="text-[#f76497] mt-0">
                            {validationErrors["triggerValue"]}
                          </p>
                        )}
                        <Dropdown>
                          <DropdownTrigger className="w-full">
                            <Button className="mb-8" variant="faded">
                              <div className="w-full flex justify-between">
                                <div className="flex justify-start w-full items-start">
                                  {data?.triggerValue || (data?.dependentField ? "Select a trigger value" : "Select a dependent field first")}
                                </div>
                                <div className="flex justify-end w-full items-end">
                                  <FaChevronDown />
                                </div>
                              </div>
                            </Button>
                          </DropdownTrigger>
                          <DropdownMenu
                            aria-label="Static Actions"
                            className="w-144 custom-dropdown-menu"
                            selectionMode="single"
                            style={{ zIndex: 9999 }}
                            onSelectionChange={(event) => {
                              const selectedValue = Array.from(event)[0];
                              setData({
                                ...data,
                                triggerValue: selectedValue,
                              });
                            }}
                          >
                            {dependentFieldOptions?.length > 0 ? (
                              dependentFieldOptions?.map((option) => (
                                <DropdownItem key={option.name}>
                                  {option.name}
                                </DropdownItem>
                              ))
                            ) : (
                              <DropdownItem key="no-options" isReadOnly>
                                {data?.dependentField ? "No options available" : "Select a dependent field first"}
                              </DropdownItem>
                            )}
                          </DropdownMenu>
                        </Dropdown>
                      </div>

                      <div className="flex flex-col gap-2 text-sm w-full">
                        <p className="mb-2">Field Type to Show</p>
                        <Select
                          label="Field Type"
                          placeholder="Select field type"
                          variant="faded"
                          selectionMode="single"
                          labelPlacement="outside"
                          className="mb-8"
                          onSelectionChange={(event) => {
                            setData({
                              ...data,
                              dependentFieldType: Array.from(event)
                                .join(", ")
                                .replaceAll("_", " "),
                            });
                          }}
                        >
                          <SelectItem key="text" value="text">Text Input</SelectItem>
                          <SelectItem key="textarea" value="textarea">Text Area</SelectItem>
                          <SelectItem key="integer" value="integer">Integer</SelectItem>
                          <SelectItem key="dropdown" value="dropdown">Dropdown</SelectItem>
                          <SelectItem key="file" value="file">File Upload</SelectItem>
                        </Select>
                      </div>

                      {data?.dependentFieldType === "dropdown" && (
                        <Textarea
                          className="mb-8"
                          label="Dependent Dropdown Options"
                          variant="faded"
                          labelPlacement="outside"
                          value={data?.dependentFieldConfig || ""}
                          placeholder="option1, option2, option3"
                          onChange={(event) =>
                            setData({ ...data, dependentFieldConfig: event.target.value })
                          }
                        />
                      )}
                    </>
                  )}

                  {/* These fields for specific Metric Selector Field  */}

                  {fieldType === "metricSelector" && (
                    <>
                      <div className="flex flex-col gap-2 text-sm w-full">
                        <p>Metric Category Filter (Optional)</p>
                        <div className="text-xs text-gray-600 mb-4">
                          Choose which categories of metrics should be listed in this field. You can select multiple categories or leave empty to show all metrics.
                          {!isLoadingCategories && dynamicCategories.length > 0 && (
                            <span className="ml-2 text-emerald-600 font-medium">
                              ({dynamicCategories.length} categories available)
                            </span>
                          )}
                          {categoriesError && (
                            <span className="ml-2 text-orange-600 font-medium">
                              (Using fallback categories)
                            </span>
                          )}
                        </div>
                        
                        {/* Select All / Clear All Controls */}
                        <div className="flex gap-2 mb-3">
                          <Button
                            size="sm"
                            variant="flat"
                            color="success"
                            onClick={() => {
                              const allCategories = dynamicCategories.map(cat => cat.name);
                              setData({
                                ...data,
                                metricCategoryFilter: allCategories,
                              });
                            }}
                            className="text-xs"
                          >
                            Select All Categories
                          </Button>
                          <Button
                            size="sm"
                            variant="flat"
                            color="danger"
                            onClick={() => {
                              setData({
                                ...data,
                                metricCategoryFilter: "",
                              });
                            }}
                            className="text-xs"
                          >
                            Clear All
                          </Button>
                        </div>
                        
                        {/* Category Selection */}
                        <Dropdown>
                          <DropdownTrigger className="w-full">
                            <Button className="mb-4" variant="faded" isLoading={isLoadingCategories}>
                              <div className="w-full flex justify-between">
                                <div className="flex justify-start w-full items-start">
                                  {isLoadingCategories ? "Loading categories..." : (
                                    data?.metricCategoryFilter ? (
                                      Array.isArray(data.metricCategoryFilter) ? 
                                        `${data.metricCategoryFilter.length} categories selected` : 
                                        data.metricCategoryFilter
                                    ) : "Show all metric categories"
                                  )}
                                </div>
                                <div className="flex justify-end w-full items-end">
                                  <FaChevronDown />
                                </div>
                              </div>
                            </Button>
                          </DropdownTrigger>
                          <DropdownMenu
                            aria-label="Metric Category Filter"
                            className="w-144"
                            selectionMode="multiple"
                            style={{ zIndex: 9999 }}
                            selectedKeys={data?.metricCategoryFilter ? new Set(Array.isArray(data.metricCategoryFilter) ? data.metricCategoryFilter : [data.metricCategoryFilter]) : new Set()}
                            onSelectionChange={(event) => {
                              const selectedValues = Array.from(event);
                              setData({
                                ...data,
                                metricCategoryFilter: selectedValues.length > 0 ? selectedValues : "",
                              });
                            }}
                          >
                            {isLoadingCategories ? (
                              <DropdownItem key="loading" isReadOnly>
                                Loading categories...
                              </DropdownItem>
                            ) : dynamicCategories.length === 0 ? (
                              <DropdownItem key="no-categories" isReadOnly>
                                No categories available
                              </DropdownItem>
                            ) : (
                              dynamicCategories.map((category) => (
                                <DropdownItem key={category.name} value={category.name}>
                                  {category.name}
                                </DropdownItem>
                              ))
                            )}
                          </DropdownMenu>
                        </Dropdown>
                        
                        {/* Live Preview */}
                        <div className="mb-3 p-3 bg-emerald-50 border border-emerald-200 rounded-lg">
                          <div className="text-xs text-emerald-800">
                            <span className="font-medium">Preview:</span> 
                            {data?.metricCategoryFilter && Array.isArray(data.metricCategoryFilter) && data.metricCategoryFilter.length > 0 ? (
                              <span> Metrics from {data.metricCategoryFilter.length} selected categories will be available</span>
                            ) : (
                              <span> All available metrics will be shown</span>
                            )}
                          </div>
                        </div>
                        
                        <div className="text-xs text-gray-500">
                          When users fill out forms with this field, they will only see metrics from the selected categories. 
                          If no categories are selected, all available metrics will be shown.
                        </div>
                      </div>
                    </>
                  )}

                  {/* Framework Field */}
                  {category?.toLowerCase() !== "reference data" && (
                    <>
                      <div className="my-12">
                        <MultiSelectInput
                          data={FrameworkSelectfieldSchema}
                          value={frameWorkData}
                          valuesList={frameWorkList}
                          onChangeHandler={(title, value, type) =>
                            setFrameWorkData(value)
                          }
                          validationErrors={validationErrors}
                        />
                      </div>
                      {frameWorkData?.map((id) => (
                        <FrameworkInputFieldAccodian
                          id={+id}
                          data={data}
                          setData={setData}
                          frameWorkDetails={frameWorkDetails}
                        />
                      ))}
                    </>
                  )}

                  {/* Group Assignment - for all fields except subheadings */}
                  {fieldType !== "subheading" && (
                    <div className="flex flex-col gap-2 text-sm w-full mb-4">
                      <p>Assign to Group (Optional)</p>
                      <Dropdown>
                        <DropdownTrigger className="w-full">
                          <Button variant="faded">
                            <div className="w-full flex justify-between">
                              <div className="flex justify-start w-full items-start">
                                {data?.assignedGroup || "No group assignment"}
                              </div>
                              <div className="flex justify-end w-full items-end">
                                <FaChevronDown />
                              </div>
                            </div>
                          </Button>
                        </DropdownTrigger>
                        <DropdownMenu
                          aria-label="Group Assignment"
                          className="w-144"
                          selectionMode="single"
                          style={{ zIndex: 9999 }}
                          onSelectionChange={(event) => {
                            const selectedValue = Array.from(event)[0];
                            setData({
                              ...data,
                              assignedGroup: selectedValue || "",
                            });
                          }}
                        >
                          <DropdownItem key="">No group assignment</DropdownItem>
                          {existingFields
                            .filter(field => field.Type === "subheading")
                            .map((field) => (
                              <DropdownItem key={field.Title}>
                                {field.Title}
                              </DropdownItem>
                            ))}
                        </DropdownMenu>
                      </Dropdown>
                      <p className="text-xs text-gray-500">
                        Choose which subheading group this field should appear under in the JSON output
                      </p>
                    </div>
                  )}

                  {(fieldType !== "link" && fieldType !== "subheading") && (
                    <div className="flex items-center">
                      <div className="flex-1">
                        <p className="mb-2">{"Make Required"}</p>
                        <Switch
                          defaultSelected={data?.required}
                          color="primary"
                          className="mb-4"
                          label="Make Required"
                          labelPlacement="outside"
                          onChange={(event) =>
                            setData({ ...data, required: !data?.required })
                          }
                        >
                          {data?.required ? "On" : "Off"}
                        </Switch>
                      </div>
                      {(fieldType === "dropdown" ||
                        fieldType === "relation") && (
                        <div className="flex-1">
                          <p className="mb-2">{"Multi-Select"}</p>
                          <Switch
                            defaultSelected={data?.isMultiSelect}
                            color="primary"
                            className="mb-4"
                            // label="Make Required"
                            labelPlacement="outside"
                            onChange={(event) =>
                              setData({
                                ...data,
                                isMultiSelect: !data?.isMultiSelect,
                              })
                            }
                          >
                            {data?.isMultiSelect ? "Yes" : "No"}
                          </Switch>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </ModalBody>
              <ModalFooter className="px-6 py-4">
                <div className="flex gap-3 justify-end w-full">
                  <Button 
                    color="default" 
                    variant="bordered" 
                    onPress={onClose}
                    className="px-6"
                  >
                    Cancel
                  </Button>
                  <Button
                    color="primary"
                    onClick={() => submitHandler(onClose)}
                    onKeyDown={(event) => handleKeyDown(event, onClose)}
                    className="px-6 bg-emerald-600 hover:bg-emerald-700"
                  >
                    Add Field
                  </Button>
                </div>
              </ModalFooter>
            </div>
          )}
        </ModalContent>
      </Modal>
    </>
  );
};
export default AddModal;
